import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, CheckCircle2, Loader2, ShieldCheck, UploadCloud,
  BadgeCheck, UserPlus, Sparkles, Briefcase, CalendarRange, FileText, X,
  Users, ClipboardEdit, Search, Mail,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import { usePublicConfig } from "../components/calculators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const PROFESSIONS = ["Pharmacist", "Pharmacy Technician", "Other"];
const EXPERIENCE = ["Newly qualified", "1–2 years", "3–5 years", "6–10 years", "10+ years"];
const ARRANGEMENTS = ["Full-time", "Part-time", "Flexible", "Open to discussion"];

const MAX_CV_BYTES = 5 * 1024 * 1024;
const CV_EXT_RX = /\.(pdf|doc|docx)$/i;

const LOOKING_FOR = [
  { icon: BadgeCheck, color: "#10B981", title: "Qualified Pharmacists",
    text: "GPhC-registered pharmacists who are passionate about delivering safe, professional and patient-focused pharmacy services." },
  { icon: ClipboardEdit, color: "#06B6D4", title: "Pharmacy Technicians",
    text: "Registered pharmacy technicians looking to apply their skills across professional pharmacy settings." },
  { icon: Sparkles, color: "#F59E0B", title: "Newly Qualified Professionals",
    text: "Newly qualified pharmacists and pharmacy technicians taking the next step in their professional journey." },
  { icon: Briefcase, color: "#EF4444", title: "Experienced Pharmacy Professionals",
    text: "Experienced pharmacists and technicians seeking new opportunities to develop their practice." },
  { icon: CalendarRange, color: "#6366F1", title: "Flexible Working",
    text: "Candidates seeking full-time, part-time or flexible opportunities, where available." },
];

const NEXT_STEPS = [
  { icon: UploadCloud, title: "Submit Your Application",
    text: "Complete the application form and upload your CV as a PDF or Word document." },
  { icon: Search, title: "We Review Your Details",
    text: "Our team carefully reviews your application, experience and professional interests." },
  { icon: Mail, title: "We Get In Touch",
    text: "If your experience matches a suitable opportunity, a member of our team will contact you." },
];

const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/* ----------------------------- Application form ----------------------------- */
const ApplicationForm = ({ privacyNotice, privacyUrl }) => {
  const empty = {
    full_name: "", email: "", phone: "", profession: "Pharmacist",
    registration_number: "", experience_years: "", work_arrangement: "",
    preferred_location: "", cover_message: "", consent: false, website: "",
  };
  const [form, setForm] = useState(empty);
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pickFile = (file) => {
    setCvError("");
    if (!file) return;
    if (!CV_EXT_RX.test(file.name)) {
      setCvFile(null);
      setCvError("Please upload your CV as a PDF or Microsoft Word document (.pdf, .doc or .docx).");
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      setCvFile(null);
      setCvError(`"${file.name}" is larger than the 5MB limit. Please upload a smaller file.`);
      return;
    }
    setCvFile(file);
  };

  const clearFile = () => {
    setCvFile(null);
    setCvError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cvFile) {
      setCvError("Please attach your CV before submitting your application.");
      fileRef.current?.focus();
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      data.append("full_name", form.full_name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("profession", form.profession);
      data.append("registration_number", form.registration_number);
      data.append("experience_years", form.experience_years);
      data.append("work_arrangement", form.work_arrangement);
      data.append("preferred_location", form.preferred_location);
      data.append("cover_message", form.cover_message);
      data.append("consent", form.consent ? "true" : "false");
      data.append("website", form.website);
      data.append("cv", cvFile);
      await axios.post(`${API}/careers/applications`, data);
      setSent(true);
      toast.success("Application submitted — thank you.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="careers-success" className="bg-white border border-[#10B981]/30 rounded-3xl p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Application received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you for your application. We have received your details and will review them.
          If your experience matches a suitable opportunity, a member of our team will contact you.
        </p>
        <button data-testid="careers-new-application-button" onClick={() => { setSent(false); setForm(empty); clearFile(); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="careers-application-form" noValidate={false}
      className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Your details</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cr-name" className={labelCls}>Full name *</label>
          <input id="cr-name" data-testid="careers-input-full-name" required minLength={2} autoComplete="name"
            value={form.full_name} onChange={upd("full_name")} placeholder="Amina Shah" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cr-email" className={labelCls}>Email address *</label>
          <input id="cr-email" data-testid="careers-input-email" type="email" required autoComplete="email"
            value={form.email} onChange={upd("email")} placeholder="a.shah@example.co.uk" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cr-phone" className={labelCls}>Telephone number *</label>
          <input id="cr-phone" data-testid="careers-input-phone" type="tel" required minLength={5} autoComplete="tel"
            value={form.phone} onChange={upd("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cr-profession" className={labelCls}>Profession *</label>
          <select id="cr-profession" data-testid="careers-select-profession" required
            value={form.profession} onChange={upd("profession")} className={inputCls}>
            {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="cr-reg" className={labelCls}>Professional registration number <span className="normal-case font-medium text-[#94A3B8]">(if applicable)</span></label>
          <input id="cr-reg" data-testid="careers-input-registration" value={form.registration_number}
            onChange={upd("registration_number")} placeholder="e.g. GPhC 2211445" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cr-experience" className={labelCls}>Years of experience</label>
          <select id="cr-experience" data-testid="careers-select-experience" value={form.experience_years}
            onChange={upd("experience_years")} className={inputCls}>
            <option value="">Select…</option>
            {EXPERIENCE.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="cr-arrangement" className={labelCls}>Preferred working arrangement</label>
          <select id="cr-arrangement" data-testid="careers-select-arrangement" value={form.work_arrangement}
            onChange={upd("work_arrangement")} className={inputCls}>
            <option value="">Select…</option>
            {ARRANGEMENTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="cr-location" className={labelCls}>Preferred location or branch <span className="normal-case font-medium text-[#94A3B8]">(if applicable)</span></label>
          <input id="cr-location" data-testid="careers-input-location" value={form.preferred_location}
            onChange={upd("preferred_location")} placeholder="e.g. Wigan, Greater Manchester" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cr-message" className={labelCls}>Cover message</label>
          <textarea id="cr-message" data-testid="careers-input-cover-message" rows={5} maxLength={4000}
            value={form.cover_message} onChange={upd("cover_message")}
            placeholder="Tell us a little about your experience, your professional interests and the kind of opportunity you are looking for…"
            className={`${inputCls} resize-none`} />
        </div>
      </div>

      <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Your CV</p>
      <div className="mt-4">
        <label htmlFor="cr-cv" className={labelCls}>Upload your CV *</label>
        <input
          ref={fileRef}
          id="cr-cv"
          data-testid="careers-input-cv"
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="sr-only"
          aria-describedby="cr-cv-help cr-cv-error"
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
        {cvFile ? (
          <div data-testid="careers-cv-confirmation" role="status"
            className="flex flex-wrap items-center gap-4 bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl px-5 py-4">
            <span className="w-11 h-11 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0">
              <FileText size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-[#0B192C] truncate">{cvFile.name}</p>
              <p className="text-xs text-[#475569] mt-0.5">Ready to upload · {fmtSize(cvFile.size)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="careers-cv-replace" onClick={() => fileRef.current?.click()}
                className="text-xs font-bold text-[#0B192C] border border-[#E2E8F0] bg-white rounded-full px-4 py-2 hover:border-[#10B981] hover:text-[#10B981] transition-colors">
                Replace file
              </button>
              <button type="button" data-testid="careers-cv-remove" onClick={clearFile} aria-label="Remove selected file"
                className="w-9 h-9 rounded-full border border-[#E2E8F0] bg-white text-[#64748B] flex items-center justify-center hover:border-[#EF4444] hover:text-[#EF4444] transition-colors">
                <X size={15} />
              </button>
            </div>
          </div>
        ) : (
          <button type="button" data-testid="careers-cv-dropzone" onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-[#E2E8F0] rounded-2xl px-6 py-8 text-center transition-colors duration-300 hover:border-[#10B981] hover:bg-[#F0FDF4]/40 focus:outline-none focus:border-[#10B981]">
            <UploadCloud size={26} className="mx-auto text-[#10B981]" />
            <span className="mt-3 block text-sm font-bold text-[#0B192C]">Choose your CV to upload</span>
            <span id="cr-cv-help" className="mt-1 block text-xs text-[#64748B]">PDF or Microsoft Word (.pdf, .doc, .docx) · Maximum file size 5MB</span>
          </button>
        )}
        {cvError && (
          <p id="cr-cv-error" data-testid="careers-cv-error" role="alert" className="mt-3 text-xs font-semibold text-[#EF4444]">
            {cvError}
          </p>
        )}
      </div>

      <div className="hidden" aria-hidden="true">
        <label htmlFor="cr-web">Website</label>
        <input id="cr-web" data-testid="careers-honeypot" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={upd("website")} />
      </div>

      <div className="mt-7 bg-[#F1F5F9] rounded-2xl px-5 py-5">
        <label htmlFor="cr-consent" className="flex items-start gap-3 cursor-pointer">
          <input id="cr-consent" data-testid="careers-input-consent" type="checkbox" required
            checked={form.consent} onChange={upd("consent")}
            className="mt-0.5 w-[18px] h-[18px] rounded accent-[#10B981] cursor-pointer" />
          <span className="text-xs leading-relaxed text-[#475569]">
            I consent to National Clinical Services Ltd storing and reviewing the information I have
            provided, including my CV, for recruitment purposes. *
          </span>
        </label>
        <p className="mt-3 pl-8 text-xs leading-relaxed text-[#64748B]" data-testid="careers-privacy-notice">
          {privacyNotice || "The information you provide, including your CV, will be used by National Clinical Services Ltd for recruitment purposes and handled in accordance with our privacy policy."}{" "}
          {privacyUrl ? (
            privacyUrl.startsWith("/") ? (
              <Link to={privacyUrl} data-testid="careers-privacy-link"
                className="font-bold text-[#0B192C] underline decoration-[#10B981] decoration-2 underline-offset-2 hover:text-[#10B981] transition-colors">
                Read our privacy policy
              </Link>
            ) : (
              <a href={privacyUrl} data-testid="careers-privacy-link" target="_blank" rel="noreferrer"
                className="font-bold text-[#0B192C] underline decoration-[#10B981] decoration-2 underline-offset-2 hover:text-[#10B981] transition-colors">
                Read our privacy policy
              </a>
            )
          ) : (
            <a href="#privacy-policy" data-testid="careers-privacy-link" onClick={(e) => e.preventDefault()}
              title="Privacy policy link coming soon"
              className="font-bold text-[#94A3B8] underline decoration-dotted decoration-[#94A3B8] underline-offset-2 cursor-default">
              Read our privacy policy
            </a>
          )}
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="careers-submit"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Submit Your Application <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* ---------------------------------- Page ---------------------------------- */
const Careers = () => {
  const config = usePublicConfig();

  return (
    <main data-testid="careers-page">
      <Seo
        title="Join Our Pharmacy Team | Pharmacist & Pharmacy Technician Careers at NCS"
        description="Build your future with National Clinical Services. We welcome applications from qualified and newly qualified pharmacists and pharmacy technicians — submit your CV today."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#10B981]">
              <Users size={13} /> Careers at NCS
            </span>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0B192C]">
            <MaskedLine delay={0.05}>Join Our Pharmacy Team</MaskedLine>
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-4 font-display text-lg md:text-xl font-bold text-[#10B981]">Build Your Future With Us</p>
            <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-[#475569]">
              We are always interested in hearing from qualified pharmacists and pharmacy technicians
              who are passionate about delivering safe, professional and patient-focused pharmacy services.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <button onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="careers-hero-cta"
              className="group btn-shine mt-8 inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C]">
              Apply Now <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-12 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
              <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Introduction</p>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#475569] max-w-3xl">
                If you are looking for a new opportunity, complete the application form and upload your CV.
                Our team will review your details and contact you if a suitable position becomes available.
              </p>
              <div className="mt-6 flex items-start gap-3 bg-[#F8FAFC] rounded-2xl px-5 py-4 max-w-3xl">
                <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed text-[#475569]">
                  Submitting an application expresses your interest in working with NCS. It does not
                  guarantee that a vacancy is currently available — we will contact you if your
                  experience matches a suitable opportunity.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO WE ARE LOOKING FOR */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#10B981]">Who we are looking for</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C] max-w-2xl">
              Applications are welcome from
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LOOKING_FOR.map((c, i) => (
              <Reveal key={c.title} delay={0.05 * i}>
                <div className="group h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(11,25,44,0.15)] hover:border-[#10B981]/40"
                  data-testid={`careers-looking-for-${i}`}>
                  <span className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${c.color}14`, color: c.color }}>
                    <c.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#0B192C]">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">{c.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <div className="h-full bg-[#0B192C] rounded-3xl p-7 flex flex-col justify-between">
                <div>
                  <UserPlus size={22} className="text-[#10B981]" />
                  <h3 className="mt-5 font-display text-lg font-bold text-white">Ready to apply?</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">
                    Complete the application form below and upload your CV. Our team will review your details.
                  </p>
                </div>
                <button onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="careers-looking-for-cta"
                  className="group mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#10B981] hover:text-white transition-colors">
                  Go to the application form <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="py-16 sm:py-20 bg-white border-y border-[#E2E8F0] scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Application form</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]">
              Tell us about yourself
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#475569] max-w-xl mx-auto leading-relaxed">
              Complete the form below and upload your CV. Fields marked * are required.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <ApplicationForm privacyNotice={config.careers_privacy_notice} privacyUrl={config.privacy_policy_url} />
          </Reveal>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#F59E0B]">What happens next</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]">
              A simple, transparent process
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {NEXT_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={0.06 * i}>
                <div className="relative bg-white border border-[#E2E8F0] rounded-3xl p-7 h-full" data-testid={`careers-step-${i}`}>
                  <span className="absolute top-6 right-7 font-display text-4xl font-extrabold text-[#F1F5F9] select-none" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="w-12 h-12 rounded-2xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                    <s.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#0B192C]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Careers;
